/**
 * MESSAGE BUBBLE COMPONENT
 * 
 * Renders different types of messages:
 * - text: Regular chat message
 * - inquiry: Initial message with attached items
 * - item_added: System message when items are added
 * - creation_added: System message when creations are added
 * - status_update: System message when item status changes
 * - item_removed: System message when item is removed
 * - creation_removed: System message when creation is removed
 */

import React from 'react';
import type { Message } from '../../messaging/types';
import ItemChip from './ItemChip';
import { AppIcons } from '../../constants/icons';

interface MessageBubbleProps {
  message: Message;
  isOwnMessage: boolean;
  senderName?: string;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ 
  message, 
  isOwnMessage,
  senderName 
}) => {
  // Format timestamp
  const formatTime = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  // System messages (status updates, item added/removed)
  if (message.type === 'status_update') {
    return (
      <div className="flex justify-center my-4">
        <div className="bg-slate-800/40 rounded-full px-4 py-2 text-sm text-glass-secondary font-body flex items-center gap-2">
          <AppIcons.StatusUpdated className="w-4 h-4 text-current" aria-label="Status updated" />
          {senderName || 'Someone'} marked "{message.statusChange?.itemName}" as{' '}
          <span className="font-bold capitalize">{message.statusChange?.newStatus}</span>
        </div>
      </div>
    );
  }

  if (message.type === 'item_added') {
    return (
      <div className="my-4">
        <div className="flex justify-center mb-2">
          <div className="bg-slate-800/40 rounded-full px-4 py-2 text-sm text-glass-secondary font-body flex items-center gap-2">
            <AppIcons.Add className="w-4 h-4 text-current" aria-label="Items added" />
            {senderName || 'Someone'} added items to the conversation
          </div>
        </div>

        {/* Show added items */}
        {message.attachedItems && message.attachedItems.length > 0 && (
          <div className="flex flex-wrap gap-2 justify-center">
            {message.attachedItems.map((item, index) => (
              <ItemChip key={`${item.itemId}-${index}`} item={item} size="small" />
            ))}
          </div>
        )}
      </div>
    );
  }

  if (message.type === 'creation_added') {
    return (
      <div className="my-4">
        <div className="flex justify-center mb-2">
          <div className="bg-purple-900/40 rounded-full px-4 py-2 text-sm text-purple-300 font-body flex items-center gap-2">
            <AppIcons.CreationsAdded className="w-4 h-4 text-current" aria-label="Creations added" />
            {senderName || 'Someone'} added creations to the conversation
          </div>
        </div>

        {/* Show added creations */}
        {message.attachedCreations && message.attachedCreations.length > 0 && (
          <div className="flex flex-wrap gap-2 justify-center">
            {message.attachedCreations.map((creation, index) => (
              <div
                key={`${creation.creationId}-${index}`}
                className="inline-flex items-center gap-2 bg-purple-900/40 border border-purple-500 rounded-lg px-2 py-1"
              >
                {creation.photoUrl ? (
                  <img
                    src={creation.photoUrl}
                    alt={creation.title}
                    className="w-8 h-8 object-cover rounded"
                  />
                ) : (
                  <div className="w-8 h-8 bg-gradient-to-br from-purple-400 to-pink-600 rounded flex items-center justify-center">
                    <AppIcons.CreationsAdded className="w-4 h-4 text-white" aria-label="Creation" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-purple-200 truncate font-body">
                    {creation.title}
                  </p>
                  {creation.price && (
                    <p className="text-xs text-green-300 font-body">
                      ${creation.price.toFixed(2)}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (message.type === 'item_removed') {
    return (
      <div className="flex justify-center my-4">
        <div className="bg-slate-800/40 rounded-full px-4 py-2 text-sm text-glass-muted font-body flex items-center gap-2">
          <AppIcons.Close className="w-4 h-4 text-current" aria-label="Item removed" />
          {senderName || 'Someone'} removed "{message.statusChange?.itemName}" from the conversation
        </div>
      </div>
    );
  }

  if (message.type === 'creation_removed') {
    return (
      <div className="flex justify-center my-4">
        <div className="bg-purple-900/40 rounded-full px-4 py-2 text-sm text-purple-300 font-body flex items-center gap-2">
          <AppIcons.Close className="w-4 h-4 text-current" aria-label="Creation removed" />
          {senderName || 'Someone'} removed "{message.statusChange?.itemName}" from the conversation
        </div>
      </div>
    );
  }

  // Inquiry message
  if (message.type === 'inquiry') {
    return (
      <div className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} mb-4`}>
        <div className={`max-w-[80%] ${isOwnMessage ? 'order-1' : ''}`}>
          {/* Inquiry header */}
          <div className={`rounded-t-2xl px-4 py-2 ${isOwnMessage ? 'bg-slate-700 text-white rounded-tr-none' : 'bg-slate-600 text-white rounded-tl-none'}`}>
            <p className="text-xs font-bold mb-1 font-body flex items-center gap-2">
              <AppIcons.DestashInquiry className="w-4 h-4 text-current" aria-label="Inquiry" />
              Inquiry about:
            </p>
            <div className="flex flex-wrap gap-2">
              {message.attachedItems?.map((item, index) => (
                <div
                  key={`${item.itemId}-${index}`}
                  className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-white ${isOwnMessage ? 'bg-slate-600' : 'bg-slate-500'}`}
                >
                  {item.photoUrl && (
                    <img src={item.photoUrl} alt="" className="w-5 h-5 rounded object-cover" />
                  )}
                  <span className="font-bold">{item.name}</span>
                </div>
              ))}
              {message.attachedCreations?.map((creation, index) => (
                <div
                  key={`${creation.creationId}-${index}`}
                  className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-white ${isOwnMessage ? 'bg-slate-600' : 'bg-slate-500'}`}
                >
                  {creation.photoUrl && (
                    <img src={creation.photoUrl} alt="" className="w-5 h-5 rounded object-cover" />
                  )}
                  <span className="font-bold">{creation.title}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Message text */}
          {message.text && (
            <div className={`rounded-b-2xl px-4 py-3 ${isOwnMessage ? 'bg-slate-700 text-white' : 'bg-slate-600 text-white'}`}>
              <p className="font-body">{message.text}</p>
            </div>
          )}

          {/* Timestamp */}
          <p className={`text-xs text-glass-muted mt-1 font-body ${isOwnMessage ? 'text-right' : 'text-left'}`}>
            {formatTime(message.timestamp)}
          </p>
        </div>
      </div>
    );
  }

  // Regular text message
  return (
    <div className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} mb-4`}>
      <div className={`max-w-[80%]`}>
        <div className={`rounded-2xl px-4 py-3 ${
          isOwnMessage
            ? 'bg-slate-700 text-white rounded-br-none'
            : 'bg-slate-600 text-white rounded-bl-none'
        }`}>
          <p className="font-body">{message.text}</p>
        </div>

        {/* Timestamp */}
        <p className={`text-xs text-glass-muted mt-1 font-body ${isOwnMessage ? 'text-right' : 'text-left'}`}>
          {formatTime(message.timestamp)}
        </p>
      </div>
    </div>
  );
};

export default MessageBubble;
